// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useI18n } from '@/i18n';
import { getSharedBffWebSocket } from '@/services/bffWebSocket';
import type { UserSession } from '@/app/user-context';

const SERVICE_HEALTH_ROLES = ['developer', 'admin'] as const;

type ServiceHealthPayload = {
  services?: Array<{ name?: string; ok?: boolean; statusCode?: number | null; url?: string | null }>;
  Services?: Array<{ Name?: string; Ok?: boolean; StatusCode?: number | null; Url?: string | null }>;
  checkedAt?: string;
  CheckedAt?: string;
  hasWarnings?: boolean;
  HasWarnings?: boolean;
  warnings?: Array<{ timestamp?: string; serviceName?: string; body?: string }>;
  Warnings?: Array<{ Timestamp?: string; ServiceName?: string; Body?: string }>;
};

type ServiceWarning = { timestamp: string; service: string; body: string };
type ServiceHealthMissingEntry = {
  name: string;
  url: string | null;
  host: string | null;
  statusCode: number | null;
  envVar: string | null;
  resolvedIp: string | null;
};

type UseServiceHealthParams = {
  apiBaseUrl: string;
  user: UserSession | null;
};

const decodeBase64Url = (value: string): string | null => {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
  if (typeof atob === 'function') {
    return atob(padded);
  }
  const buffer = (globalThis as { Buffer?: { from: (value: string, encoding: string) => { toString: (encoding: string) => string } } }).Buffer;
  if (buffer?.from) {
    return buffer.from(padded, 'base64').toString('utf-8');
  }
  return null;
};

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  const parts = token.split('.');
  if (parts.length < 2) return null;
  const decoded = decodeBase64Url(parts[1]);
  if (!decoded) return null;
  try {
    return JSON.parse(decoded) as Record<string, unknown>;
  } catch {
    return null;
  }
};

const extractKeycloakRoles = (payload: Record<string, unknown>): Set<string> => {
  const roles = new Set<string>();
  const realmAccess = payload.realm_access as { roles?: unknown } | undefined;
  if (realmAccess?.roles && Array.isArray(realmAccess.roles)) {
    realmAccess.roles.forEach((role) => {
      if (typeof role === 'string') roles.add(role);
    });
  }
  const resourceAccess = payload.resource_access as Record<string, { roles?: unknown }> | undefined;
  if (resourceAccess) {
    Object.values(resourceAccess).forEach((entry) => {
      if (!entry?.roles || !Array.isArray(entry.roles)) return;
      entry.roles.forEach((role) => {
        if (typeof role === 'string') roles.add(role);
      });
    });
  }
  return roles;
};

const normalizeRole = (value: string): string => value.trim().toLowerCase();

const matchesRequiredRole = (actualRole: string, requiredRole: string): boolean => {
  const actual = normalizeRole(actualRole);
  const required = normalizeRole(requiredRole);
  return actual === required || actual.endsWith(`:${required}`) || actual.endsWith(`.${required}`) || actual.endsWith(`_${required}`) || actual.endsWith(`-${required}`);
};

const hasKeycloakRole = (token: string | null | undefined, role: string): boolean => {
  if (!token) return false;
  const payload = decodeJwtPayload(token);
  if (!payload) return false;
  const roles = extractKeycloakRoles(payload);
  for (const actualRole of roles) {
    if (matchesRequiredRole(actualRole, role)) {
      return true;
    }
  }
  return false;
};

const hasAnyServiceHealthRole = (token: string | null | undefined): boolean =>
  SERVICE_HEALTH_ROLES.some((role) => hasKeycloakRole(token, role));

export const useServiceHealth = ({ apiBaseUrl, user }: UseServiceHealthParams) => {
  const { t } = useI18n();
  const [serviceStatusOpen, setServiceStatusOpen] = useState(false);
  const [serviceStatusMissing, setServiceStatusMissing] = useState<ServiceHealthMissingEntry[]>([]);
  const [serviceStatusCheckedAt, setServiceStatusCheckedAt] = useState<string | null>(null);
  const [serviceStatusHasWarnings, setServiceStatusHasWarnings] = useState(false);
  const [serviceStatusFetchFailed, setServiceStatusFetchFailed] = useState(false);
  const [serviceStatusFlashOn, setServiceStatusFlashOn] = useState(true);
  const [serviceStatusWarnings, setServiceStatusWarnings] = useState<ServiceWarning[]>([]);
  const lastServiceHealthFallbackRef = useRef(0);

  const canCheckServiceHealth = useMemo(() => hasAnyServiceHealthRole(user?.token), [user?.token]);

  const applyServiceHealthSnapshot = useCallback((data: ServiceHealthPayload) => {
    const services = data.services ?? data.Services ?? [];
    const toPascal = (value: string): string =>
      value
        .split(/[^a-zA-Z0-9]+/)
        .filter((segment) => segment.length > 0)
        .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase())
        .join('');

        const missing = services
          .filter((service) => (service.ok ?? (service as { Ok?: boolean }).Ok) === false)
          .map((service) => {
            const name = service.name ?? (service as { Name?: string }).Name ?? '';
            const url = service.url ?? (service as { Url?: string | null }).Url ?? null;
            const statusCode =
              (service.statusCode ?? (service as { StatusCode?: number }).StatusCode) ?? null;
            const resolvedIp = (service as { resolvedIp?: string })?.resolvedIp ?? null;
            let host: string | null = null;
            if (url) {
              try {
                host = new URL(url).host;
              } catch {
                host = url;
              }
            }
            const cleanName = name.replace(/-service$/i, '');
            if (!host && cleanName) {
              host = `${cleanName}.svc.goffy.local`;
            }
            const envVar = cleanName ? `${toPascal(cleanName)}__BaseUrl` : null;
            return { name, url, host, statusCode, envVar, resolvedIp };
          })
          .filter((entry): entry is ServiceHealthMissingEntry => !!entry.name);
    setServiceStatusMissing(missing);
    setServiceStatusCheckedAt((data.checkedAt ?? data.CheckedAt) ?? null);
    setServiceStatusHasWarnings((data.hasWarnings ?? data.HasWarnings) ?? false);
    const rawWarnings = data.warnings ?? data.Warnings ?? [];
    const normalizedWarnings = rawWarnings.map((entry) => ({
      timestamp: entry.timestamp ?? (entry as { Timestamp?: string }).Timestamp ?? '',
      service: entry.serviceName ?? (entry as { ServiceName?: string }).ServiceName ?? '',
      body: entry.body ?? (entry as { Body?: string }).Body ?? '',
    }));
    setServiceStatusWarnings(normalizedWarnings);
    setServiceStatusFetchFailed(false);
  }, []);

  useEffect(() => {
    if (!user?.token || !canCheckServiceHealth) {
      setServiceStatusMissing([]);
      setServiceStatusCheckedAt(null);
      setServiceStatusHasWarnings(false);
      setServiceStatusFetchFailed(false);
      setServiceStatusWarnings([]);
      setServiceStatusOpen(false);
      return;
    }

    let cancelled = false;
    const client = getSharedBffWebSocket(apiBaseUrl);
    client.setAuthToken(user.token);

    const fetchServiceHealthFallback = async () => {
      const now = Date.now();
      if (now - lastServiceHealthFallbackRef.current < 5000) return;
      lastServiceHealthFallbackRef.current = now;
      try {
        const response = await fetch(`${apiBaseUrl}/api/bff/services/health`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) throw new Error('health_failed');
        const data = (await response.json()) as ServiceHealthPayload;
        if (!cancelled) {
          applyServiceHealthSnapshot(data);
        }
      } catch {
        if (!cancelled) {
          setServiceStatusMissing([{ name: t('serviceStatus.bffName'), url: null }]);
          setServiceStatusCheckedAt(null);
          setServiceStatusHasWarnings(false);
          setServiceStatusWarnings([]);
          setServiceStatusFetchFailed(true);
        }
      }
    };

    const unsubscribeMessage = client.onMessage((message) => {
      const payload = message as { type?: string };
      if (!payload.type || payload.type !== 'service.health.update') return;
      applyServiceHealthSnapshot(message as ServiceHealthPayload);
    });

    const unsubscribeError = client.onError(() => {
      void fetchServiceHealthFallback();
    });
    const unsubscribeConnection = client.onConnectionChange((state) => {
      if (state === 'disconnected') {
        void fetchServiceHealthFallback();
      }
    });

    return () => {
      cancelled = true;
      unsubscribeMessage();
      unsubscribeError();
      unsubscribeConnection();
    };
  }, [apiBaseUrl, applyServiceHealthSnapshot, canCheckServiceHealth, t, user?.token]);

  useEffect(() => {
    if (!serviceStatusFetchFailed) {
      setServiceStatusFlashOn(true);
      return;
    }
    const timer = setInterval(() => {
      setServiceStatusFlashOn((prev) => !prev);
    }, 600);
    return () => clearInterval(timer);
  }, [serviceStatusFetchFailed]);

  return {
    canCheckServiceHealth,
    serviceStatusOpen,
    setServiceStatusOpen,
    serviceStatusMissing,
    serviceStatusCheckedAt,
    serviceStatusHasWarnings,
    serviceStatusFetchFailed,
    serviceStatusFlashOn,
    serviceStatusWarnings,
    applyServiceHealthSnapshot,
  };
};
